import { ViewTypeName } from '@air/api/types';
import {
  CheckIcon,
  LayoutGridIcon as GridIcon,
  type IconComponent,
  KanbanIcon,
  ListIcon,
  SquareKanbanIcon,
  Table2Icon,
} from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { DropdownMenuWithActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { preventCloseAutoFocusIfFocusMovedOutside } from '~/utils/preventCloseAutoFocusIfFocusMovedOutside';

export type ViewTypeToggleMenuComponentProps = {
  availableViewTypeNames: ViewTypeName[];
  currentViewTypeName: ViewTypeName;
  dataTestId?: string;
  onChange: (nextViewTypeName: ViewTypeName) => void;
};

type ViewTypeView = {
  icon: IconComponent;
  label: string;
};

const VIEW_TYPE_VIEW_MAP: Record<ViewTypeName, ViewTypeView> = {
  [ViewTypeName.gallery]: {
    icon: GridIcon,
    label: 'Gallery',
  },
  [ViewTypeName.table]: {
    icon: ListIcon,
    label: 'Table',
  },
  [ViewTypeName.kanban]: {
    icon: KanbanIcon,
    label: 'Kanban',
  },
};

// The redesign swaps Table and Kanban for the prototype's Lucide glyphs; Gallery already matches.
const UI2026_VIEW_TYPE_VIEW_MAP: Record<ViewTypeName, ViewTypeView> = {
  [ViewTypeName.gallery]: {
    icon: GridIcon,
    label: 'Gallery',
  },
  [ViewTypeName.table]: {
    icon: Table2Icon,
    label: 'Table',
  },
  [ViewTypeName.kanban]: {
    icon: SquareKanbanIcon,
    label: 'Kanban',
  },
};

export const ViewTypeToggleMenuComponent = ({
  availableViewTypeNames,
  onChange,
  currentViewTypeName,
  dataTestId,
}: ViewTypeToggleMenuComponentProps) => {
  // Read here rather than at the call sites: this menu is shared by private board/library clusters, the public board
  // header and showcases, and the flag applies on public surfaces too, so every host would otherwise have to
  // thread the same value through.
  const { ui2026 } = useTemporaryFeatureFlags();
  const activeViewTypeViewMap = ui2026 ? UI2026_VIEW_TYPE_VIEW_MAP : VIEW_TYPE_VIEW_MAP;
  const { icon: currentIcon, label: currentLabel } = activeViewTypeViewMap[currentViewTypeName];

  const viewTypeOptions: DropdownMenuOption[] = availableViewTypeNames.map((viewTypeName) => {
    const { icon: OptionIcon, label } = activeViewTypeViewMap[viewTypeName];

    return {
      id: viewTypeName,
      label: label,
      onSelect: () => onChange(viewTypeName),
      prefix: <OptionIcon size="sm" />,
      suffix: viewTypeName === currentViewTypeName ? <CheckIcon size="sm" /> : null,
      type: 'item',
    };
  });

  // The primitive renders a title option as a label (not a menu item), so it doesn't shift item ids or selection.
  const options: DropdownMenuOption[] = ui2026
    ? [{ id: 'view-as', label: 'View as', type: 'title' }, ...viewTypeOptions]
    : viewTypeOptions;

  return (
    <DropdownMenuWithActionSheet
      align="end"
      onCloseAutoFocus={preventCloseAutoFocusIfFocusMovedOutside}
      options={options}
      size="small"
      trigger={
        <IconButton
          appearance="ghost"
          color="gray"
          label={currentLabel}
          adsIcon
          icon={currentIcon}
          size="small"
          data-testid={dataTestId}
        />
      }
      title="View Type"
    />
  );
};
